import Image from 'next/image'
import Link from '@/components/ui/Link'
import { getReadableImageUrl } from '@/lib/directus'
import { getBlogPostUrl } from '@/lib/blog'
import { htmlToPlainText, createExcerpt } from '../utils'
import { useState, useEffect } from 'react'
import BlogTabs from '../BlogTabs/BlogTabs'
import BlogOrder from '../BlogOrder/BlogOrder'
import { useRouter } from 'next/router'
import AuthorDisplay from '../AuthorDisplay'

const formatDate = (dateString: string) => {
	const date = new Date(dateString)
	return date.toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	})
}

export interface BlogIndexSectionData {
	posts: {
		id: string
		title: string
		slug: string
		content: string
		published_at: string
		author: {
			slug: string
			title: string
		}
		featured_image: {
			id: string
			filename_disk: string
		}
	}[]
	total: number
	totalPages: number
	currentPage: number
	categories: {
		id: string
		slug: string
		title: string
	}[]
	archives: {
		date: string
		count: number
	}[]
	left_column_data?: any
}

interface BlogIndexSectionProps {
	data: BlogIndexSectionData
}

export default function BlogIndexSection({ data }: BlogIndexSectionProps) {
	const [mounted, setMounted] = useState(false)
	const [displayedPosts, setDisplayedPosts] = useState(data.posts)
	const [visiblePosts, setVisiblePosts] = useState(6) // Start with 6 posts
	const [isLoading, setIsLoading] = useState(false)
	const router = useRouter()

	useEffect(() => {
		setMounted(true)
	}, [])

	useEffect(() => {
		// Sort posts based on URL sort parameter
		const sortOrder = router.query.sort as string || 'newest'
		const sortedPosts = [...data.posts].sort((a, b) => {
			const dateA = new Date(a.published_at).getTime()
			const dateB = new Date(b.published_at).getTime()
			return sortOrder === 'newest' ? dateB - dateA : dateA - dateB
		})
		setDisplayedPosts(sortedPosts)
	}, [router.query.sort, data.posts])

	const handleLoadMore = () => {
		setIsLoading(true)
		// Simulate loading delay
		setTimeout(() => {
			setVisiblePosts(prev => prev + 6) // Load 1 more post
			setIsLoading(false)
		}, 500)
	}

	if (!mounted) return null

	const shownPosts = displayedPosts.slice(0, visiblePosts)
	const hasMorePosts = visiblePosts < displayedPosts.length

	return (
		<section
			id='blog-index'
			className='BlogIndexSection BlogIndexSectionMain py-16'
		>
			<div className='container mx-auto max-w-[1400px] px-4'>
				<div className='flex flex-col'>
					<div className='BlogNav mb-16 max-md:justify-center max-md:flex flex-wrap'>
						<div className='flex justify-between items-center'>
							<BlogTabs
								data={{
									categories: data.categories,
									archives: data.archives,
								}}
								activeCategorySlug={undefined}
							/>
						</div>
						<div className='flex justify-end items-center mt-8'>
							<BlogOrder />
						</div>
					</div>

					<div>
						<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10'>
							{shownPosts.map(post => (
								<article
									key={post.id}
									className='overflow-hidden flex flex-col h-full'
								>
									<Link href={getBlogPostUrl(post.slug)} className='block'>
										<div className='relative aspect-[16/10] w-full'>
											<Image
												src={
													post.featured_image
														? getReadableImageUrl(
																post.featured_image.id,
																post.featured_image.filename_disk
														  )
														: '/images/blog-placeh2.webp'
												}
												alt={htmlToPlainText(post.title) || ''}
												fill
												className='object-cover'
												sizes='(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw'
												quality={95}
												loading={'lazy'}
											/>
										</div>
									</Link>
									<div className='py-8 flex flex-col flex-grow'>
										<h2 className='text-3xl md:text-4xl mb-6'>
											<Link
												href={getBlogPostUrl(post.slug)}
												className='hover:text-brown'
											>
												{htmlToPlainText(post.title)}
											</Link>
										</h2>

									<div className='prose prose-neutral max-w-none mb-6 flex-grow'>
										<p className='text-base line-clamp-[6]'>
											{createExcerpt(post.content, 400)}
										</p>
									</div>

										<div className='grid'>
											<div>
												<AuthorDisplay author={post.author} />
											</div>
											{/* <div className='flex gap-2'>
												<span>
													<time dateTime={post.published_at}>
														{mounted ? formatDate(post.published_at) : ''}
													</time>
												</span>
												<span>•</span>
												<span>5 min read</span>
											</div> */}
										</div>
									</div>
								</article>
							))}
						</div>

						{hasMorePosts && (
							<div className='mt-12 text-center'>
								<button
									onClick={handleLoadMore}
									disabled={isLoading}
									className='w-full px-12 py-3 bg-brown-light rounded-lg hover:bg-brown text-neutral-50 transition-colors text-2xl font-brandon text-center max-w-[340px] mx-auto'
								>
									{isLoading ? 'Loading...' : 'Load More'}
								</button>
								<p className='mt-4 text-sm text-neutral-600'>
									Showing {visiblePosts} of {displayedPosts.length} posts
								</p>
							</div>
						)}
					</div>
				</div>
			</div>
		</section>
	)
}
